import type { IImmunizationInfo } from 'src/interfaces/family'
import {
  getImmunizationRecordStatus,
  type ImmunizationRecordStatus,
} from 'src/utils/immunizationStatus'

export const vaccinationTrackingConfigurationPath =
  'features.vaccinationTracking'

export function getImmunizationReportColumnLabel(
  enableVaccinationTracking: boolean,
) {
  return enableVaccinationTracking
    ? 'Vaccination Status'
    : 'Immunizations Up To Date'
}

export type OverallVaccinationStatus =
  | 'Complete'
  | 'Complete/Exempt'
  | 'Exempt'
  | 'Alternative'
  | 'Complete/Exempt/Alternative'
  | 'Overdue'
  | 'Missing Info'
  | 'Overdue/Missing Info'

export interface OverallVaccinationStatusMeta {
  label: OverallVaccinationStatus
  color: string
  textColor: string
  icon: string
}

export function getOverallVaccinationStatus(
  immunizationInfo?: IImmunizationInfo,
): OverallVaccinationStatus {
  const statuses =
    immunizationInfo?.immunizationRecord?.map((record) =>
      getImmunizationRecordStatus(record),
    ) ?? []

  if (statuses.length === 0) return 'Missing Info'

  return getOverallVaccinationStatusFromRecordStatuses(statuses)
}

export function getOverallVaccinationStatusFromRecordStatuses(
  statuses: ImmunizationRecordStatus[],
): OverallVaccinationStatus {
  const hasOverdue = statuses.includes('Overdue')
  const hasMissingInfo = statuses.includes('Missing Info')

  if (hasOverdue && hasMissingInfo) return 'Overdue/Missing Info'
  if (hasOverdue) return 'Overdue'
  if (hasMissingInfo) return 'Missing Info'

  const hasComplete = statuses.includes('Complete')
  const hasUpcoming = statuses.includes('Upcoming')
  const hasExempt = statuses.includes('Exempt')
  const hasAlternative = statuses.includes('Alternative')

  if (hasAlternative && (hasComplete || hasUpcoming || hasExempt))
    return 'Complete/Exempt/Alternative'
  if (hasAlternative) return 'Alternative'

  if ((hasComplete || hasUpcoming) && hasExempt) return 'Complete/Exempt'
  if (hasExempt) return 'Exempt'

  return 'Complete'
}

export function getOverallVaccinationStatusMeta(
  immunizationInfo?: IImmunizationInfo,
): OverallVaccinationStatusMeta {
  const status = getOverallVaccinationStatus(immunizationInfo)

  if (status === 'Overdue' || status === 'Overdue/Missing Info') {
    return {
      label: status,
      color: 'negative',
      textColor: 'white',
      icon: 'error',
    }
  }

  if (status === 'Missing Info') {
    return {
      label: status,
      color: 'grey-5',
      textColor: 'white',
      icon: 'help_outline',
    }
  }

  if (status.includes('Exempt') || status.includes('Alternative')) {
    return {
      label: status,
      color: 'orange',
      textColor: 'white',
      icon: status.includes('Alternative') ? 'event_note' : 'privacy_tip',
    }
  }

  return {
    label: status,
    color: 'positive',
    textColor: 'white',
    icon: 'check_circle',
  }
}
